import SEO from "../components/SEO";
import Breadcrumbs from "../components/layout/Breadcrumbs";
import SectionHeading from "../components/blocks/SectionHeading";
import FAQ from "../components/blocks/FAQ";
import EnquiryForm from "../components/blocks/EnquiryForm";
import { ORG_CAPABILITIES, ORG_FAQS } from "../data/content";

const ENQUIRY_FIELDS = [
  { name: "name", label: "Name", required: true },
  { name: "email", label: "Email", type: "email", required: true },
  { name: "phone", label: "Phone", type: "tel" },
  { name: "organisation", label: "Organisation", required: true },
  { name: "role", label: "Role", required: true },
  { name: "industry", label: "Industry" },
  { name: "employees", label: "Approximate Employee Count", type: "select", options: ["1–10", "11–50", "51–200", "201–1,000", "1,001–5,000", "5,000+"] },
  { name: "capability", label: "Capability Requirement", type: "select", required: true, options: ORG_CAPABILITIES.concat(["Other / Not sure yet"]) },
  { name: "audience", label: "Target Audience", type: "select", options: ["Senior Leadership", "Middle Management", "Frontline Managers", "Sales Teams", "Cross-functional Teams", "Whole Organisation"] },
  { name: "participants", label: "Number of Participants" },
  { name: "outcome", label: "Desired Outcome", type: "textarea", full: true, required: true },
  { name: "timeline", label: "Timeline", type: "select", options: ["Immediately", "Within 3 months", "3–6 months", "6–12 months", "Exploratory"] },
];

export default function OrganisationsPage() {
  return (
    <>
      <SEO
        title="For Organisations — Corporate Capability Building | The Vantage Institute™"
        description="Build capability that moves the business: leadership development, strategic thinking, manager effectiveness, sales excellence, customer centricity, AI & digital capability and transformation."
        path="/organisations"
      />
      <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "For Organisations" }]} />

      <section className="bg-navy">
        <div className="container-x py-16 sm:py-24">
          <p className="eyebrow-light">For Organisations</p>
          <h1 className="mt-4 max-w-3xl font-display text-4xl font-semibold leading-tight text-white sm:text-5xl">
            Build Capability That Moves the Business.
          </h1>
          <span className="gold-rule mt-8 !w-24" aria-hidden="true" />
          <p className="mt-8 max-w-2xl text-base leading-relaxed text-platinum md:text-lg">
            Vantage designs corporate learning around a single test: does it change what the business can do?
            Solutions are built with your context, your audience and your desired outcome at the centre — never
            off the shelf.
          </p>
          <a href="#enquiry" className="btn-gold mt-10 inline-flex" data-testid="org-hero-cta">
            Request a Corporate Proposal
          </a>
        </div>
      </section>

      <section className="container-x py-16 sm:py-24" data-testid="org-capabilities">
        <SectionHeading
          eyebrow="Capability Areas"
          title="Where Vantage builds organisational capability."
          intro="Each area can be delivered as a focused programme, a workshop series or a structured learning journey — designed around your business context."
        />
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {ORG_CAPABILITIES.map((c) => (
            <div key={c} className="card card-hover" data-testid={`org-capability-${c.toLowerCase().replace(/[^a-z]+/g, "-").replace(/-$/, "")}`}>
              <h3 className="font-sub text-base font-semibold text-navy">{c}</h3>
              <span className="gold-rule mt-3 !w-10" aria-hidden="true" />
            </div>
          ))}
        </div>
      </section>

      <section className="bg-paper" data-testid="org-approach">
        <div className="container-x py-16 sm:py-24">
          <SectionHeading
            eyebrow="How Engagements Work"
            title="From conversation to capability."
          />
          <ol className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { n: "01", t: "Corporate Conversation", d: "We start with your business context: the outcome that matters, the audience and the gap between the two." },
              { n: "02", t: "Solution Design", d: "The Institute designs a programme, journey or workshop series around your context — content, format, rhythm and measurement." },
              { n: "03", t: "Delivery & Application", d: "Learning is delivered against live business situations, so capability is built in the work, not beside it." },
              { n: "04", t: "Review & Embed", d: "We review what changed, what stuck and what needs reinforcement — capability is only real when it endures." },
            ].map((s) => (
              <li key={s.n} className="border-t-2 border-gold bg-white p-6" data-testid={`org-step-${s.n}`}>
                <p className="font-display text-2xl font-semibold text-gold-deep" aria-hidden="true">{s.n}</p>
                <h3 className="mt-3 font-sub text-base font-semibold text-navy">{s.t}</h3>
                <p className="mt-2.5 text-sm leading-relaxed text-slate">{s.d}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section id="enquiry" className="container-x scroll-mt-24 py-16 sm:py-24" data-testid="org-enquiry-section">
        <div className="grid gap-12 lg:grid-cols-[1fr_1.4fr]">
          <SectionHeading
            eyebrow="Start the Conversation"
            title="Request a corporate proposal."
            intro="Tell us about your organisation and the capability you need to build. The Institute will respond with a considered point of view — not a brochure."
          />
          <div className="card">
            <EnquiryForm
              fields={ENQUIRY_FIELDS}
              submitLabel="Request a Corporate Proposal"
              testId="corporate-enquiry-form"
            />
          </div>
        </div>
      </section>

      <FAQ items={ORG_FAQS} title="Working With Vantage" />
    </>
  );
}
